import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiFileText, FiPlus } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import { toast } from 'react-toastify';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import ATable, { ATableHeader } from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import { signatureApi, ApiError } from '../../../services/signatureApi';

const STATUSES = [
    { value: 'DRAFT', label: 'Rascunho' },
    { value: 'SENT', label: 'Enviado' },
    { value: 'IN_PROGRESS', label: 'Em andamento' },
    { value: 'COMPLETED', label: 'Concluído' },
    { value: 'CANCELLED', label: 'Cancelado' },
    { value: 'REJECTED', label: 'Rejeitado' },
    { value: 'EXPIRED', label: 'Expirado' },
    { value: 'FROZEN', label: 'Congelado' },
    { value: 'ERROR', label: 'Erro' },
    { value: 'CLOSED', label: 'Encerrado' },
];

const STATUS_COLORS: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    SENT: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    IN_PROGRESS: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    COMPLETED: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    CANCELLED: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    EXPIRED: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
    FROZEN: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
    ERROR: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    CLOSED: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};

const formatDate = (v: string | null) => {
    if (!v) return '-';
    return new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const columns: ATableHeader[] = [
    { id: 'op', title: 'Operação', field: 'operationNumber', canSort: false, width: '140px' },
    { id: 'person', title: 'Cliente', field: 'personName', canSort: false },
    { id: 'taxId', title: 'CPF/CNPJ', field: 'personGovTaxId', canSort: false, width: '150px' },
    { id: 'status', title: 'Status', field: 'status', canSort: false, cssClass: 'text-center', width: '140px' },
    { id: 'created', title: 'Criado em', field: 'createdAt', canSort: false, width: '160px' },
    { id: 'updated', title: 'Atualizado', field: 'updatedAt', canSort: false, width: '160px' },
];

const SignaturePageList = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [refreshKey, setRefreshKey] = useState(0);
    const pageSize = 100;

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await signatureApi.getProcessList({
                page, size: pageSize,
                status: statusFilter || undefined,
                search: search || undefined,
            });
            setRows(result.data || []);
            setTotal(result.total || 0);
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            toast.error('Erro ao carregar processos de assinatura');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, statusFilter, search, navigate]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <div className="space-y-1">
                    <button onClick={() => setStatusFilter('')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!statusFilter
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{total}</span>
                        </div>
                    </button>
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3 mt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3 uppercase">Status</div>
                        <div className="space-y-1">
                            {STATUSES.map(s => (
                                <button key={s.value} onClick={() => { setStatusFilter(s.value); setPage(1); }}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${statusFilter === s.value
                                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    return (
        <>
            {isLoading && <Loading />}

            <button onClick={() => navigate('/signature/create')}
                className="fixed top-[69px] right-[10px] w-10 h-10 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-50 flex items-center justify-center border-2 border-white dark:border-slate-800 bg-blue-600 hover:bg-blue-700 text-white"
                title="Nova Assinatura">
                <FiPlus size={14} />
            </button>

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center space-x-2">
                        <FiFileText className="text-blue-600" size={18} />
                        <span>Assinaturas</span>
                    </div>
                }
                leftSidebar={<FilterSidebar filterGroups={filterGroups} />}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    {/* Header */}
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Assinaturas Digitais</h1>
                            <div className="flex items-center space-x-2">
                                <button onClick={() => setRefreshKey(k => k + 1)}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <div className="w-80">
                                    <TextEdit label="Buscar operação ou pessoa..." value={searchInput}
                                        onChange={(e: any) => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="flex-1 overflow-auto">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns} fieldSorted={sortField} orderSorted={sortOrder}
                                onSort={(f: string, o: 'ASC' | 'DESC') => { setSortField(f); setSortOrder(o); }}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={6} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhum processo encontrado</td></tr>
                                ) : rows.map((r: any, i: number) => (
                                    <tr key={r.signatureProcessUuid}
                                        className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${i % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}
                                        onClick={() => navigate(`/signature/${r.signatureProcessUuid}`)}>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-medium text-blue-600 dark:text-blue-400">{r.operationNumber || '-'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white truncate">{r.personName || '-'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm text-gray-500 dark:text-gray-400">{r.personGovTaxId || '-'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[r.status] || ''}`}>
                                                {STATUSES.find(s => s.value === r.status)?.label || r.status}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm text-gray-500 dark:text-gray-400">{formatDate(r.createdAt)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm text-gray-500 dark:text-gray-400">{formatDate(r.updatedAt)}</td>
                                    </tr>
                                ))}
                            </ATable>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800">
                        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                            <span>{total} processo(s)</span>
                            <div className="flex gap-1">
                                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                                    className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-slate-700 dark:border-slate-600">Anterior</button>
                                <span className="px-3 py-1">{page} / {Math.ceil(total / pageSize) || 1}</span>
                                <button disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)}
                                    className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-slate-700 dark:border-slate-600">Próximo</button>
                            </div>
                        </div>
                    </div>
                </div>
            </ThreeColumnLayout>
        </>
    );
};

export default SignaturePageList;
